import React, { useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  TextField, 
  IconButton, 
  InputAdornment,
  Drawer,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Badge,
  Avatar,
  Tooltip,
  Fade,
  Grow,
  Collapse
} from "@mui/material";
import { 
  Send as SendIcon,
  Report as ReportIcon,
  Chat as ChatIcon,
  Search as SearchIcon,
  Menu as MenuIcon,
  MoreVert as MoreVertIcon,
  AttachFile as AttachFileIcon,
  EmojiEmotions as EmojiIcon,
  Mic as MicIcon,
  ArrowBack as ArrowBackIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Home as HomeIcon
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";


const chats = [
  { 
    id: 1, 
    name: "Ganza", 
    lastMessage: "Mwiriwe?", 
    time: "3:55 PM", 
    unread: 2, 
    online: true,
    avatar: ''
  },
  { 
    id: 2, 
    name: "D'amour", 
    lastMessage: "Muraho neza?", 
    time: "3:52 PM", 
    unread: 0, 
    online: false,
    avatar: ''
  },
  { 
    id: 3, 
    name: "Claver", 
    lastMessage: "Hello", 
    time: "3:36 PM", 
    unread: 5, 
    online: true,
    avatar: ''
  },
  { 
    id: 4, 
    name: "Claude", 
    lastMessage: "Hey", 
    time: "3:36 PM", 
    unread: 0, 
    online: false,
    avatar: ''
  },
];

const theme = createTheme({
  palette: {
    primary: {
      main: '#0B5ED7',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          transition: 'width 0.3s ease !important',
          overflowX: 'hidden',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize: '1.5rem',
          width: 48,
          height: 48,
        },
      },
    },
  },
});

const ChatContainer = styled(Box)(({ theme }) => ({
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default,
}));

const MessageBubble = styled(Box)(({ sender, theme }) => ({
  maxWidth: '70%',
  padding: '8px 12px',
  borderRadius: sender === 'You' ? '18px 18px 0 18px' : '18px 18px 18px 0',
  backgroundColor: sender === 'You' ? theme.palette.primary.main : '#e0e0e0',
  color: sender === 'You' ? '#fff' : '#000',
  marginBottom: '12px',
  alignSelf: sender === 'You' ? 'flex-end' : 'flex-start',
  boxShadow: '0 1px 1px rgba(0,0,0,0.1)',
  wordBreak: 'break-word',
  animation: 'fadeIn 0.3s ease',
  position: 'relative',
  '@keyframes fadeIn': {
    from: { opacity: 0, transform: sender === 'You' ? 'translateX(20px)' : 'translateX(-20px)' },
    to: { opacity: 1, transform: 'translateX(0)' },
  },
}));

const MessageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  padding: '0 8px',
});

const MessageWithAvatar = styled(Box)({
  display: 'flex',
  alignItems: 'flex-end',
  gap: '8px',
  marginBottom: '8px',
  '&.you': {
    flexDirection: 'row-reverse',
  }
});

const DashboardLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  color: theme.palette.primary.main,
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  marginBottom: theme.spacing(2),
}));

export default function ChatSection() {
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleSendMessage = () => {
    if (input.trim()) {
      const newMessage = { 
        text: input, 
        sender: "You", 
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isYou: true 
      };
      setMessages([...messages, newMessage]);
      setInput("");
      
      setTimeout(() => {
        const replies = [
          "Hello there!",
          "How are you doing?",
          "Nice to hear from you!",
          "I'll get back to you soon.",
          "Thanks for your message!"
        ];
        const reply = replies[Math.floor(Math.random() * replies.length)];
        setMessages(prev => [...prev, { 
          text: reply, 
          sender: selectedChat?.name || "Them", 
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          isYou: false,
          avatar: selectedChat?.avatar || '👤'
        }]);
      }, 1000);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  useEffect(() => {
    if (selectedChat) {
      const messageContainer = document.getElementById('message-container');
      if (messageContainer) {
        messageContainer.scrollTop = messageContainer.scrollHeight;
      }
    }
  }, [messages, selectedChat]);

  useEffect(() => {
    if (selectedChat && isMobile) {
      setMobileOpen(false);
    }
  }, [selectedChat, isMobile]);

  const drawer = (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: '#fff',
      boxShadow: '0 0 10px rgba(0,0,0,0.1)',
      width: sidebarCollapsed ? '80px' : '350px',
      transition: 'width 0.3s ease',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        background: 'linear-gradient(195deg, #0a2e52 0%, #1565C0 100%)', 
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '64px',
      }}>
        {!sidebarCollapsed && (
          <Typography variant="h6" display="flex" alignItems="center">
            <ChatIcon sx={{ mr: 1 }} /> Chats
          </Typography>
        )}
        {sidebarCollapsed && (
          <Box display="flex" justifyContent="center" width="100%">
            <ChatIcon />
          </Box>
        )}
        {!sidebarCollapsed && (
          <Box>
            <IconButton color="inherit" size="small">
              <GroupIcon fontSize="small" />
            </IconButton>
            <IconButton color="inherit" size="small">
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
      
      {/* Dashboard Link */}
      <Box sx={{ 
        px: sidebarCollapsed ? 1 : 2,
        pt: 1,
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        pb: 2,
      }}>
        <DashboardLink to="/PendingTickets">
          <HomeIcon fontSize={sidebarCollapsed ? "medium" : "small"} />
          {!sidebarCollapsed && (
            <Typography variant="body2" sx={{ ml: 1 }}>
              Back to Dashboard
            </Typography>
          )}
        </DashboardLink>
      </Box>
      
      {/* Search */}
      <Collapse in={!sidebarCollapsed} orientation="horizontal">
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              sx: { 
                borderRadius: '20px',
                backgroundColor: '#f5f5f5',
                '& fieldset': { border: 'none' }
              }
            }}
          />
        </Box>
      </Collapse>
      
      {/* Chat list */}
      <List sx={{ 
        flex: 1, 
        overflowY: 'auto',
        px: sidebarCollapsed ? 0 : 2,
      }}>
        {chats
          .filter((chat) => chat.name.toLowerCase().includes(search.toLowerCase()))
          .map((chat) => (
            <Grow in={true} key={chat.id}>
              <ListItem
                sx={{ 
                  cursor: 'pointer', 
                  backgroundColor: selectedChat?.id === chat.id ? '#e3f2fd' : 'inherit',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  transition: 'background-color 0.2s',
                  py: 2,
                  px: sidebarCollapsed ? '8px' : 2,
                  borderRadius: '8px',
                  flexDirection: sidebarCollapsed ? 'column' : 'row',
                  alignItems: sidebarCollapsed ? 'center' : 'flex-start',
                  justifyContent: 'center',
                }}
                onClick={() => setSelectedChat(chat)}
              >
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  variant="dot"
                  color="success"
                  invisible={!chat.online}
                >
                  <Avatar sx={{ mr: sidebarCollapsed ? 0 : 2 }}>
                    {chat.avatar}
                  </Avatar>
                </Badge>
                
                {!sidebarCollapsed && (
                  <Box sx={{ flex: 1 }}>
                    <Box display="flex" alignItems="center">
                      <Typography fontWeight="medium">{chat.name}</Typography>
                      <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
                        {chat.time}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {chat.lastMessage}
                    </Typography>
                  </Box>
                )}
                
                {!sidebarCollapsed && chat.unread > 0 && (
                  <Box sx={{ 
                    ml: 2,
                    minWidth: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                  }}>
                    {chat.unread}
                  </Box>
                )}
                
                {sidebarCollapsed && chat.unread > 0 && (
                  <Box sx={{ 
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                  }} />
                )}
              </ListItem>
            </Grow>
          ))}
      </List>
      
      {/* Collapse button */}
      <Box sx={{ 
        p: 1, 
        display: 'flex', 
        justifyContent: 'flex-end',
        borderTop: '1px solid rgba(0,0,0,0.1)',
      }}>
        <Tooltip title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
          <IconButton onClick={toggleSidebar} size="small">
            {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ChatContainer>
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Mobile drawer */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: 280 
              },
            }}
          >
            {drawer}
          </Drawer>
          
          {/* Desktop sidebar */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': { 
                position: 'relative',
                width: sidebarCollapsed ? 80 : 350,
              },
            }}
            open
          >
            {drawer}
          </Drawer>
          
          {/* Main chat area */}
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            backgroundColor: '#f5f5f5',
          }}>
            {selectedChat ? (
              <>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  {isMobile ? (
                    <>
                      <IconButton onClick={handleDrawerToggle} sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                      </IconButton>
                      <DashboardLink to="/dashboard" sx={{ 
                        color: 'text.primary',
                        p: 0,
                        mr: 2,
                      }}>
                        <HomeIcon fontSize="medium" />
                      </DashboardLink>
                    </>
                  ) : (
                    <IconButton 
                      onClick={toggleSidebar} 
                      sx={{ 
                        mr: 1,
                        display: { xs: 'none', md: 'inline-flex' }
                      }}
                    >
                      <MenuIcon />
                    </IconButton>
                  )}
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    variant="dot"
                    color="success"
                    invisible={!selectedChat.online}
                  >
                    <Avatar sx={{ width: 40, height: 40, mr: 2 }}>
                      {selectedChat.avatar}
                    </Avatar>
                  </Badge>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight="medium">{selectedChat.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedChat.online ? 'Online' : 'Offline'}
                    </Typography>
                  </Box>
                  <IconButton>
                    <MoreVertIcon />
                  </IconButton>
                </Box>
                
                <Box 
                  id="message-container"
                  sx={{ 
                    flex: 1, 
                    p: 2, 
                    overflowY: 'auto',
                    backgroundImage: 'linear-gradient(rgba(255,255,255,0.9), rgba(255,255,255,0.9)), url("https://www.transparenttextures.com/patterns/light-wool.png")',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <MessageContainer>
                    {messages.map((msg, index) => (
                      <MessageWithAvatar key={index} className={msg.isYou ? 'you' : ''}>
                        {!msg.isYou && (
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {msg.avatar || '👤'}
                          </Avatar>
                        )}
                        <Fade in={true}>
                          <MessageBubble sender={msg.isYou ? 'You' : 'Them'}>
                            {!msg.isYou && (
                              <Typography variant="caption" sx={{ 
                                fontWeight: 'bold',
                                color: 'inherit',
                                display: 'block',
                                mb: 0.5
                              }}>
                                {msg.sender}
                              </Typography>
                            )}
                            <Typography variant="body1">{msg.text}</Typography>
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'flex-end', 
                              alignItems: 'center',
                              mt: 0.5,
                            }}>
                              <Typography variant="caption" sx={{ 
                                opacity: 0.7,
                                fontSize: '0.7rem',
                                color: msg.isYou ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.5)',
                              }}>
                                {msg.time}
                              </Typography>
                              {msg.isYou && (
                                <Tooltip title="Report">
                                  <IconButton size="small" sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    p: 0,
                                    ml: 0.5,
                                    '&:hover': { color: '#fff' },
                                  }}>
                                    <ReportIcon fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </MessageBubble>
                        </Fade>
                      </MessageWithAvatar>
                    ))}
                  </MessageContainer>
                </Box>
                
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: '#fff',
                  boxShadow: '0 -1px 3px rgba(0,0,0,0.1)',
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    <IconButton>
                      <AttachFileIcon />
                    </IconButton>
                    <IconButton>
                      <EmojiIcon />
                    </IconButton>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type a message..."
                      multiline
                      maxRows={4}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '20px',
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                    />
                    <IconButton 
                      color="primary" 
                      onClick={handleSendMessage}
                      disabled={!input.trim()}
                    >
                      <SendIcon />
                    </IconButton>
                    <IconButton>
                      <MicIcon />
                    </IconButton>
                  </Box>
                </Box>
              </>
            ) : (
              <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                textAlign: 'center',
                p: 3,
              }}>
                <Fade in={true}>
                  <Box>
                    <ChatIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h5" color="text.secondary" gutterBottom>
                      {sidebarCollapsed ? 'Select a chat' : 'Select a chat to start messaging'}
                    </Typography>
                    {!sidebarCollapsed && (
                      <Typography variant="body1" color="text.secondary">
                        Or start a new conversation
                      </Typography>
                    )}
                    {isMobile ? (
                      <IconButton 
                        color="primary" 
                        sx={{ mt: 3 }}
                        onClick={handleDrawerToggle}
                      >
                        <MenuIcon fontSize="large" />
                      </IconButton>
                    ) : (
                      <IconButton 
                        color="primary" 
                        sx={{ mt: 3 }}
                        onClick={toggleSidebar}
                      >
                        <ChevronRightIcon fontSize="large" />
                      </IconButton>
                    )}
                  </Box>
                </Fade>
              </Box>
            )}
          </Box>
        </Box>
      </ChatContainer>
    </ThemeProvider>
  );
}